import React, { forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useAutocomplete } from '@mui/base/AutocompleteUnstyled'
import { createFilterOptions } from '@mui/material/Autocomplete'
import Tippy from '@tippyjs/react'
import withTheme from '../styles/withTheme'
import TextField from '../TextField'
import Icon from '../Icon'

import './Autocomplete.scss'

export { createFilterOptions }

const Autocomplete = forwardRef(function Autocomplete(props, ref) {
	/* eslint-disable no-unused-vars */
	const {
		autocomplete = false,
		autoHighlight = false,
		className,
		clearOnBlur = !props.freeSolo,
		blurOnSelect = false,
		debug = false,
		defaultValue = null,
		disableClearable = false,
		disabled = false,
		filterOptions,
		forcePopupIcon = 'auto',
		freeSolo,
		getOptionLabel = (option) => option.label ?? option,
		isOptionEqualToValue,
		handleHomeEndKeys = !props.freeSolo,
		helperText,
		icon,
		id: idProp,
		inputValue: inputValueProp,
		InputProps,
		isCorrect,
		label,
		ListboxComponent = 'ul',
		ListboxProps,
		loading = false,
		loadingText = 'Loading…',
		noOptionsText = 'No options',
		onChange,
		onClose,
		onHighlightChange,
		onInputChange,
		onOpen,
		open,
		openOnFocus = false,
		OptionComponent = 'li',
		OptionProps,
		options,
		renderOption: renderOptionProp,
		value: valueProp,
		...other
	} = props

	const {
		getClearProps,
		getRootProps,
		getInputLabelProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		getPopupIndicatorProps,
		focused,
		inputValue,
		anchorEl,
		setAnchorEl,
		groupedOptions,
		popupOpen,
		value
	} = useAutocomplete({ ...props, componentName: 'Vb-autocomplete' })
	/* eslint-enable no-unused-vars */

	const renderOption = (option, optionProps, state) => {
		return (
			<OptionComponent {...optionProps} className="Vb-autocomplete__option">
				{renderOptionProp ? renderOptionProp(option, state) : getOptionLabel(option)}
			</OptionComponent>
		)
	}
	const renderListOption = (option, index) => {
		const { key, ...optionProps } = getOptionProps({ option, index })
		return renderOption(
			option,
			{ ...optionProps, className: 'Vb-autocomplete__option', key: index },
			{ selected: optionProps['aria-selected'], inputValue }
		)
	}

	const hasPopupIcon = (!freeSolo || forcePopupIcon === true) && forcePopupIcon !== false

	useImperativeHandle(ref, () => ({
		clear() {
			getClearProps().onClick()
			getInputProps().ref.current.blur()
		}
	}))

	//--------------------------------------------------------
	/* Evaluate arrow-icon color */
	const arrowColor = () => {
		if (disabled) {
			return 'gray-3'
		} else if (isCorrect === false) {
			return 'error'
		} else {
			return 'primary-lima'
		}
	}

	return (
		<div
			ref={ref}
			className={clsx(
				'Vb-autocomplete',
				hasPopupIcon && 'Vb-autocomplete--has-popup-icon',
				helperText && 'Vb-autocomplete--has-helpertext',
				className
			)}
			theme={props.theme}
		>
			<Tippy
				ref={anchorEl}
				content={
					<ListboxComponent className={'Vb-autocomplete__list-box'} {...getListboxProps()} {...ListboxProps}>
						{groupedOptions.length === 0 && !freeSolo && !loading && focused ? (
							<OptionComponent className="Vb-autocomplete__no-options">{noOptionsText}</OptionComponent>
						) : null}

						{groupedOptions.length === 0 && loading ? (
							<OptionComponent className="Vb-autocomplete__loading">{loadingText}</OptionComponent>
						) : null}

						{groupedOptions.length ? groupedOptions.map((option, index) => renderListOption(option, index)) : <></>}
					</ListboxComponent>
				}
				theme="Vb-autocomplete-theme"
				visible={popupOpen}
				delay={21}
				duration={0}
				placement="bottom"
				interactive
				arrow={false}
				offset={[0, -1]}
				maxWidth="none"
			>
				<div {...getRootProps(other)}>
					<div className="Vb-autocomplete__textfield-wrapper">
						<TextField
							label={label}
							icon={icon}
							labelProps={getInputLabelProps()}
							inputProps={getInputProps()}
							InputProps={InputProps}
							className="Vb-textfield-wrapper--in-autocomplete"
							disabled={disabled}
							inputRef={setAnchorEl}
							isCorrect={isCorrect}
							helperText={helperText}
						/>
						{hasPopupIcon && !loading && (
							<Icon
								{...getPopupIndicatorProps()}
								color={arrowColor()}
								className="Vb-autocomplete__popup-icon"
							>
								{popupOpen ? 'arrow-up' : 'arrow-down'}
							</Icon>
						)}
						{hasPopupIcon && loading && <div className="Vb-autocomplete__popup-loading" />}
					</div>
				</div>
			</Tippy>
		</div>
	)
})

export default withTheme('Vb-autocomplete')(Autocomplete)

Autocomplete.defaultProps = {
	autoHighlight: false,
	blurOnSelect: false,
	debug: false,
	defaultValue: null,
	disableClearable: false,
	disabled: false,
	forcePopupIcon: 'auto',
	freeSolo: false,
	ListboxComponent: 'ul',
	loading: false,
	loadingText: 'Loading…',
	openOnFocus: false,
	OptionComponent: 'li'
}

Autocomplete.propTypes = {
	/**
	 * If `true`, the first option is automatically highlighted.
	 * @default false
	 */
	autoHighlight: PropTypes.bool,
	/**
	 * Control if the input should be blurred when an option is selected:
	 *
	 * - `false` the input is not blurred.
	 * - `true` the input is always blurred.
	 * - `touch` the input is blurred after a touch event.
	 * - `mouse` the input is blurred after a mouse event.
	 * @default false
	 */
	blurOnSelect: PropTypes.oneOfType([PropTypes.oneOf(['mouse', 'touch']), PropTypes.bool]),
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * Pass a ref to the `clear button` element.
	 */
	clearButtonRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	]),
	/**
	 * If `true`, the input's text is cleared on blur if no value is selected.
	 *
	 * Set to `true` if you want to help the user enter a new value.
	 * Set to `false` if you want to help the user resume his search.
	 * @default !props.freeSolo
	 */
	clearOnBlur: PropTypes.bool,
	/**
	 * If `true`, the popup will ignore the blur event if the input is filled.
	 * You can inspect the popup markup with your browser tools.
	 * Consider this option when you need to customize the component.
	 * @default false
	 */
	debug: PropTypes.bool,
	/**
	 * The default input value. Use when the component is not controlled.
	 * @default null
	 */
	defaultValue: PropTypes.any,
	/**
	 * If `true`, the input can't be cleared.
	 * @default false
	 */
	disableClearable: PropTypes.bool,
	/**
	 * If `true`, the input is disabled.
	 * @default false
	 */
	disabled: PropTypes.bool,
	/**
	 * A filter function that determines the options that are eligible.
	 *
	 * @param {T[]} options The options to render.
	 * @param {object} state The state of the component.
	 * @returns {T[]}
	 */
	filterOptions: PropTypes.func,
	/**
	 * Force the visibility display of the popup icon.
	 * @default 'auto'
	 */
	forcePopupIcon: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.bool]),
	/**
	 * If `true`, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
	 * @default false
	 */
	freeSolo: PropTypes.bool,
	/**
	 * Used to determine the string value for a given option.
	 * It's used to fill the input (and the list box options if `renderOption` is not provided).
	 *
	 * @param {T} option
	 * @returns {string}
	 * @default (option) => option.label ?? option
	 */
	getOptionLabel: PropTypes.func,
	/**
	 * Used to determine if an option is selected, considering the current value.
	 * Uses strict equality by default.
	 *
	 * @param {T} option The option to test.
	 * @param {T} value The value to test against.
	 * @returns {boolean}
	 */
	isOptionEqualToValue: PropTypes.func,
	/**
	 * If `true`, the component handles the "Home" and "End" keys when the popup is open.
	 * It should move focus to the first option and last option, respectively.
	 * @default !props.freeSolo
	 */
	handleHomeEndKeys: PropTypes.bool,
	/**
	 * The text of the HelperText element,
	 * that displays a helper text below the Dropdown.
	 */
	helperText: PropTypes.string,
	/**
	 * Text code from the icon list that the Icon component uses
	 * If setted, an icon will be displayed in the left side of the input
	 */
	icon: PropTypes.string,
	/**
	 * This prop is used to help implement the accessibility logic.
	 * If you don't provide this prop. It falls back to a randomly generated id.
	 */
	id: PropTypes.string,
	/**
	 * Props applied to the Input element.
	 */
	InputProps: PropTypes.object,
	/**
	 * The input value.
	 */
	inputValue: PropTypes.string,
	/**
	 * Flag to store the validation of the input
	 * and it's resulting styling
	 */
	isCorrect: PropTypes.bool,
	/**
	 * The label associated to the autocomplete
	 */
	label: PropTypes.string,
	/**
	 * The component used to render the listbox.
	 * @default 'ul'
	 */
	ListboxComponent: PropTypes.elementType,
	/**
	 * Props applied to the Listbox element.
	 */
	ListboxProps: PropTypes.object,
	/**
	 * If `true`, the component is in a loading state.
	 * @default false
	 */
	loading: PropTypes.bool,
	/**
	 * Text to display when in a loading state.
	 *
	 * @default 'Loading…'
	 */
	loadingText: PropTypes.node,
	/**
	 * Text to display when there are no options.
	 *
	 * @default 'No options'
	 */
	noOptionsText: PropTypes.node,
	/**
	 * Callback fired when the value changes.
	 *
	 * @param {object} event The event source of the callback.
	 * @param {T|T[]} value The new value of the component.
	 * @param {string} reason One of "create-option", "select-option", "remove-option", "blur" or "clear".
	 */
	onChange: PropTypes.func,
	/**
	 * Callback fired when the popup requests to be closed.
	 * Use in controlled mode (see open).
	 *
	 * @param {object} event The event source of the callback.
	 * @param {string} reason Can be: `"toggleInput"`, `"escape"`, `"select-option"`, `"remove-option"`, `"blur"`.
	 */
	onClose: PropTypes.func,
	/**
	 * Callback fired when the highlight option changes.
	 *
	 * @param {object} event The event source of the callback.
	 * @param {T} option The highlighted option.
	 * @param {string} reason Can be: `"keyboard"`, `"auto"`, `"mouse"`.
	 */
	onHighlightChange: PropTypes.func,
	/**
	 * Callback fired when the input value changes.
	 *
	 * @param {object} event The event source of the callback.
	 * @param {string} value The new value of the text input.
	 * @param {string} reason Can be: `"input"` (user input), `"reset"` (programmatic change), `"clear"`.
	 */
	onInputChange: PropTypes.func,
	/**
	 * Callback fired when the popup requests to be opened.
	 * Use in controlled mode (see open).
	 *
	 * @param {object} event The event source of the callback.
	 */
	onOpen: PropTypes.func,
	/**
	 * Control the popup` open state.
	 */
	open: PropTypes.bool,
	/**
	 * If `true`, the popup will open on input focus.
	 * @default false
	 */
	openOnFocus: PropTypes.bool,
	/**
	 * The component used to render the option.
	 * @default 'li'
	 */
	OptionComponent: PropTypes.elementType,
	/**
	 * Props applied to the option element.
	 */
	OptionProps: PropTypes.object,
	/**
	 * Array of options.
	 */
	options: PropTypes.array.isRequired,
	/**
	 * Render the option, use `getOptionLabel` by default.
	 *
	 * @param {T} option The option to render.
	 * @param {object} state The state of the component.
	 * @returns {ReactNode}
	 */
	renderOption: PropTypes.func,
	/**
	 * The value of the autocomplete.
	 *
	 * The value must have reference equality with the option in order to be selected.
	 * You can customize the equality behavior with the `isOptionEqualToValue` prop.
	 */
	value: PropTypes.any
}
